import {
  AppBar,
  Avatar,
  Box,
  ClickAwayListener,
  Fade,
  IconButton,
  Menu,
  MenuItem,
  Popper,
  Toolbar,
  Typography,
  type PopperPlacementType,
} from '@mui/material'
import { ReactComponent as DownArrowBig } from 'common/assets/down-arrow-big.svg'
import { ReactComponent as DownIcon } from 'common/assets/down_1.svg'
import { resetSpoofingToken, signOut } from 'common/authentication/auth.service'
import ThemeButton from 'common/ui/theme-button'
import { BUTTON_SIZE, BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import { findEmailInitials, findInitials } from 'common/utils/functions'
import theme from 'common/utils/theme'
import { HeaderHeight, UserApps, type HeaderLink, Role, type UserInfoData } from 'common/utils/types'
import React, { useState, type MouseEvent, type ReactElement, type ReactNode, useEffect } from 'react'
import { Link, useHref, useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { clearAuth, setSpoofingTokenToPayload } from 'common/authentication/auth.slice'

import styles from './HeaderNavigation.module.scss'

const getCircles = (userInfo: UserInfoData | null, activeAppRoute: string): string => {
  if (userInfo?.role === Role.admin) {
    return "url(\"data:image/svg+xml,%3Csvg width='109' height='98' viewBox='0 0 109 98' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cellipse opacity='0.2' cx='111' cy='40.489' rx='57' ry='56.6902' fill='%233DB19E'/%3E%3Cellipse opacity='0.2' cx='57' cy='3.69022' rx='57' ry='56.6902' fill='%233DB19E'/%3E%3C/svg%3E%0A\")"
  } else if (userInfo?.role === Role.stakeholder) {
    return "url(\"data:image/svg+xml,%3Csvg width='109' height='98' viewBox='0 0 109 98' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cellipse opacity='0.2' cx='111' cy='40.489' rx='57' ry='56.6902' fill='%23598BFF'/%3E%3Cellipse opacity='0.2' cx='57' cy='3.69022' rx='57' ry='56.6902' fill='%23598BFF'/%3E%3C/svg%3E%0A\")"
  } else if (userInfo?.role === Role.consultant) {
    return activeAppRoute === UserApps.Vendors
      ? "url(\"data:image/svg+xml,%3Csvg width='109' height='98' viewBox='0 0 109 98' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cellipse opacity='0.2' cx='111' cy='40.489' rx='57' ry='56.6902' fill='%23598BFF'/%3E%3Cellipse opacity='0.2' cx='57' cy='3.69022' rx='57' ry='56.6902' fill='%23598BFF'/%3E%3C/svg%3E%0A\")"
      : "url(\"data:image/svg+xml,%3Csvg width='109' height='98' viewBox='0 0 109 98' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cellipse opacity='0.2' cx='111' cy='40.489' rx='57' ry='56.6902' fill='%23EA9A56'/%3E%3Cellipse opacity='0.2' cx='57' cy='3.69022' rx='57' ry='56.6902' fill='%23EA9A56'/%3E%3C/svg%3E%0A\")"
  } else if (userInfo?.role === Role.vendor) {
    return "url(\"data:image/svg+xml,%3Csvg width='109' height='98' viewBox='0 0 109 98' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cellipse opacity='0.2' cx='111' cy='40.489' rx='57' ry='56.6902' fill='%23598BFF'/%3E%3Cellipse opacity='0.2' cx='57' cy='3.69022' rx='57' ry='56.6902' fill='%23598BFF'/%3E%3C/svg%3E%0A\")"
  } else {
    return 'UNKNOWN'
  }
}

const getColor = (userInfo: UserInfoData | null, activeAppRoute: string): string => {
  if (userInfo?.role === Role.admin) {
    return theme.palette.green.main
  } else if (userInfo?.role === Role.stakeholder) {
    return theme.palette.charts.purple100.main
  } else if (userInfo?.role === Role.consultant) {
    return activeAppRoute === UserApps.Vendors
      ? theme.palette.charts.purple100.main
      : theme.palette.charts.orange.main
  } else if (userInfo?.role === Role.company) {
    return activeAppRoute === UserApps.Vendors
      ? theme.palette.charts.purple100.main
      : theme.palette.charts.orange.main
  } else if (userInfo?.role === Role.vendor) {
    return theme.palette.charts.purple100.main
  } else {
    return theme.palette.gray.main
  }
}

const getImpersonatedUserRole = (role: string | undefined, activeAppRoute: string): string => {
  if (!role) {
    return ''
  }
  switch (true) {
    case role === 'admin':
      return 'Admin'
    case role === 'consultant':
      return activeAppRoute === UserApps.Vendors ? 'Vendor' : 'Konsultant'
    case role === 'company':
      return activeAppRoute === UserApps.Vendors ? 'Vendor' : 'Kompanija'
    case role === 'stakeholder':
      return 'Stakeholder'
    case role === 'vendor':
      return 'Vendor'
    default:
      return ''
  }
}

interface HeaderNavigationProps {
  logo: ReactNode
  links: HeaderLink[]
  dropdownItems: Array<{
    id: number
    logo: ReactNode
    roles: Role[]
    route: string
  }>
  resetStateExceptCategories?: () => { type: string }
  customClickAction?: () => unknown
}
const HeaderNavigation: React.FC<HeaderNavigationProps> = ({
  logo,
  links,
  dropdownItems,
  resetStateExceptCategories,
  customClickAction,
}): ReactElement => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const currentUrl = location.pathname.substring(1)
  const { userInfo, username, spoofSessionToken } = useAppSelector((state) => state.auth)

  const activeAppRoute = useHref('/')

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [nestedLinks, setNestedLinks] = useState<Array<{ label: string; route: string }> | undefined>()
  const [anchorElLinks, setAnchorElLinks] = useState<null | HTMLElement>(null)
  const [anchorElProfile, setAnchorElProfile] = useState<HTMLButtonElement | null>(null)
  const [openProfile, setOpenProfile] = useState(false)
  const [placement, setPlacement] = useState<PopperPlacementType>()
  const open = !!anchorEl

  const getRole = (roles: Role | undefined, activeAppRoute: string): string => {
    if (!roles) {
      return ''
    }
    switch (true) {
      case userInfo?.role === Role.admin:
        return 'Admin'
      case userInfo?.role === Role.company:
        return activeAppRoute === UserApps.Vendors ? 'Vendor' : 'Kompanija'
      case userInfo?.role === Role.consultant:
        return activeAppRoute === UserApps.Vendors ? 'Vendor' : 'Konsultant'
      case userInfo?.role === Role.stakeholder:
        return 'Stakeholder'
      case userInfo?.role === Role.vendor:
        return 'Vendor'
      case userInfo?.isVendor:
        return 'Vendor'
      default:
        return ''
    }
  }
  const handleCloseLinks = (): void => {
    setNestedLinks(undefined)
    setAnchorElLinks(null)
  }

  const handleClickLinks = (event: React.MouseEvent<HTMLDivElement>, link: HeaderLink): void => {
    setNestedLinks(nestedLinks && nestedLinks.length > 0 ? undefined : link.nestedRoutes)
    if (link.nestedRoutes) setAnchorElLinks(event.currentTarget)
  }

  const handleClick = (): void => {
    customClickAction
      ? customClickAction()
      : setAnchorEl(anchorEl ? null : document.getElementById('anchorElement'))
  }

  const handleClickProfile =
    (newPlacement: PopperPlacementType) => (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorElProfile(event.currentTarget)
      setOpenProfile((prev) => placement !== newPlacement || !prev)
      setPlacement(newPlacement)
    }

  const isSelectedLink = (link: HeaderLink): boolean => {
    if (link.route === currentUrl) {
      return true
    }
    return !!link.nestedRoutes?.find((link: { label: string; route: string }) => {
      return link.route === currentUrl
    })
  }

  useEffect(() => {
    if (!localStorage.getItem('spoofSessionToken')) {
      dispatch(setSpoofingTokenToPayload(undefined))
    }
  }, [])
  return (
    <AppBar
      id="anchorElement"
      style={{ background: theme.palette.blue500.main, zIndex: 1499 }}
      sx={{
        position: 'block',
        height: HeaderHeight,
        '& .MuiToolbar-gutters': {
          paddingLeft: '20px',
          paddingRight: 0,
        },
      }}
    >
      <Toolbar sx={{ paddingLeft: '20px !important' }}>
        <div className={styles.container}>
          <div className={styles.leftSide}>
            <div className={styles.headerContent}>
              <div className={styles.border}>
                <div className={styles.logo}>
                  {logo}
                  {dropdownItems.filter((item) => item.roles.some((role) => userInfo?.role === role)).length >
                    1 && (
                    <div onClick={handleClick} className={open ? styles.arrowUp : styles.arrowDown}>
                      <DownArrowBig fill={theme.palette.green.main} />
                    </div>
                  )}
                </div>
              </div>
              {links
                .filter(
                  (link) =>
                    link.roles.some((role) => userInfo?.role === role) ||
                    (link.roles.some((role) => role === Role.vendor) && userInfo?.isVendor),
                )
                .map((link, index) => (
                  <div
                    key={index}
                    className={styles.close}
                    onClick={(event) => {
                      anchorElLinks ? handleCloseLinks() : handleClickLinks(event, link)
                    }}
                  >
                    <Typography
                      variant="title2"
                      sx={isSelectedLink(link) ? { color: theme.palette.blue100.main } : { color: 'white' }}
                    >
                      {link.nestedRoutes ? (
                        <div>
                          <div
                            className={styles.link}
                            onClick={(event) => {
                              handleClickLinks(event, link)
                            }}
                          >
                            {link.label}
                            <DownIcon fill={isSelectedLink(link) ? theme.palette.blue100.main : 'white'} />
                          </div>
                          <Menu
                            anchorEl={anchorElLinks}
                            open={!!anchorElLinks}
                            onClose={handleCloseLinks}
                            sx={{
                              zIndex: 1500,
                              top: '53px',
                              '& .MuiPaper-root': {
                                width: '313px',
                                borderRadius: '5px',
                                boxShadow: 'none',
                                background: theme.palette.blue600.main,
                              },
                              '& .MuiMenuItem-root': {
                                padding: '20x 40px 8px 16px',
                                backgroundColor: theme.palette.blue500.main,
                                fontSize: '14px',
                              },
                              '& .MuiMenuItem-root:hover': {
                                background: theme.palette.blue600.main,
                              },
                              '& .MuiModal-backdrop': {
                                top: '73px',
                                background: 'rgba(16, 20, 38, 0.3)',
                              },
                              '& .MuiList-root': {
                                padding: 0,
                              },
                            }}
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                          >
                            {nestedLinks?.map((item: { label: string; route: string }, index: number) => {
                              return (
                                <Link to={item.route} key={index}>
                                  <MenuItem
                                    className={styles.dropdownItem}
                                    onClick={handleCloseLinks}
                                    sx={{
                                      padding: '10px 0 10px 17px',
                                      background:
                                        activeAppRoute === item.route
                                          ? 'rgba(49, 82, 142, 0.16)'
                                          : `${theme.palette.blue900.main}`,
                                    }}
                                  >
                                    {item.label}
                                  </MenuItem>
                                </Link>
                              )
                            })}
                          </Menu>
                        </div>
                      ) : (
                        <a
                          onClick={
                            customClickAction ??
                            (() => {
                              if (resetStateExceptCategories) {
                                dispatch(resetStateExceptCategories())
                              }
                              navigate(link.route ?? '')
                            })
                          }
                          style={{
                            cursor: 'pointer',
                          }}
                        >
                          {link.label}
                        </a>
                      )}
                    </Typography>
                  </div>
                ))}
            </div>
          </div>
          <div className={styles.rightSide}>
            <IconButton
              size="medium"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              color="inherit"
              onClick={handleClickProfile('bottom-start')}
              ref={setAnchorElProfile}
              className={spoofSessionToken ? styles.spoofAvatar : 'none'}
            >
              <Avatar sx={{ backgroundColor: theme.palette.green.main, color: theme.palette.white.main }}>
                {userInfo?.name
                  ? findInitials(userInfo.name)
                  : userInfo?.email
                  ? findEmailInitials(userInfo?.email)
                  : findEmailInitials(username)}
              </Avatar>
            </IconButton>
            <div className={styles.name}>
              <Typography variant="title2" sx={{ textAlign: 'left' }}>
                {userInfo?.name ? userInfo.name : userInfo?.email ? userInfo?.email : username}
              </Typography>
            </div>
            <Popper
              className={styles.popper}
              open={openProfile}
              anchorEl={anchorElProfile}
              placement={placement}
              transition
              style={{ zIndex: 3000 }}
            >
              {({ TransitionProps }) => (
                <ClickAwayListener
                  onClickAway={() => {
                    setOpenProfile(false)
                  }}
                >
                  <Fade {...TransitionProps}>
                    <Box
                      className={styles.popperMenu}
                      sx={{
                        backgroundImage: getCircles(userInfo, activeAppRoute),
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right top',
                        backgroundColor: theme.palette.blue500.main,
                        minHeight: '246px',
                        width: '260px',
                        marginLeft: '-25px',
                        borderRadius: '5px',
                        marginTop: '15px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          height: '80%',
                          paddingTop: '50px',
                        }}
                      >
                        <Typography variant="title2" sx={{ textAlign: 'center' }}>
                          {userInfo?.name}
                        </Typography>
                        <Typography
                          variant="title3"
                          sx={{
                            color: getColor(userInfo, activeAppRoute),
                          }}
                        >
                          {spoofSessionToken
                            ? getImpersonatedUserRole(userInfo?.role, activeAppRoute)
                            : getRole(userInfo?.role, activeAppRoute)}
                        </Typography>
                        <Typography
                          variant="subtitle3"
                          sx={{
                            color: theme.palette.blue100.main,
                            paddingTop: '30px',
                            paddingBottom: '30px',
                            textAlign: 'center',
                            width: '80%',
                            wordWrap: 'break-word',
                          }}
                        >
                          {spoofSessionToken ? userInfo?.email : username}
                        </Typography>
                        {spoofSessionToken && (
                          <ThemeButton
                            variant={BUTTON_VARIANTS.secondary}
                            sx={{ marginBottom: '45px' }}
                            size={BUTTON_SIZE.small}
                            onClick={() => {
                              void dispatch(resetSpoofingToken())
                              window.location.href = UserApps.Users
                              setOpenProfile(false)
                            }}
                          >
                            PREKINI SESIJU
                          </ThemeButton>
                        )}
                        <ThemeButton
                          variant={BUTTON_VARIANTS.secondary}
                          sx={{ marginBottom: '45px' }}
                          size={BUTTON_SIZE.small}
                          onClick={
                            customClickAction ??
                            (async () => {
                              await dispatch(resetSpoofingToken())
                              void dispatch(clearAuth())
                              void dispatch(signOut())
                            })
                          }
                        >
                          ODJAVI SE
                        </ThemeButton>
                      </Box>
                    </Box>
                  </Fade>
                </ClickAwayListener>
              )}
            </Popper>
          </div>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClick}
            sx={{
              zIndex: 1500,
              top: '57px',
              '& .MuiPaper-root': {
                width: '313px',
                left: '0px !important',
                borderRadius: 0,
                boxShadow: 'none',
                background: theme.palette.blue900.main,
              },
              '& .MuiMenuItem-root': {
                padding: '25px 0px 25px 50px',
              },
              '& .MuiMenuItem-root:hover': {
                background: 'rgba(49, 82, 142, 0.16)',
              },
              '& .MuiModal-backdrop': {
                top: '73px',
                background: 'rgba(16, 20, 38, 0.7)',
              },
              '& .MuiList-root': {
                padding: 0,
              },
            }}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {dropdownItems
              .filter((item) => item.roles.some((role) => userInfo?.role === role))
              .map((item, index) => (
                <a href={item.route} key={index}>
                  <MenuItem
                    className={styles.dropdownItem}
                    onClick={handleClick}
                    sx={{
                      background:
                        activeAppRoute === item.route
                          ? 'rgba(49, 82, 142, 0.16)'
                          : `${theme.palette.blue900.main}`,
                    }}
                  >
                    {item.logo}
                  </MenuItem>
                </a>
              ))}
          </Menu>
        </div>
      </Toolbar>
    </AppBar>
  )
}
export default HeaderNavigation
